import { Player } from '@remotion/player';
import {
  useCurrentFrame,
  AbsoluteFill,
  interpolate,
  Easing,
} from 'remotion';

// Brand Colors
const COLORS = {
  deepBlue: '#0a0f1a',
  midBlue: '#0f172a',
  cyan: '#06b6d4',
  cyanGlow: 'rgba(6, 182, 212, 0.8)',
};

// Grid line definitions
const GRID_LINES = {
  horizontal: [
    { y: '15%', x1: '0%', x2: '40%', delay: 0 },
    { y: '25%', x1: '60%', x2: '100%', delay: 30 },
    { y: '35%', x1: '0%', x2: '30%', delay: 60 },
    { y: '45%', x1: '70%', x2: '100%', delay: 90 },
    { y: '55%', x1: '0%', x2: '25%', delay: 120 },
    { y: '65%', x1: '75%', x2: '100%', delay: 150 },
    { y: '75%', x1: '0%', x2: '35%', delay: 180 },
    { y: '85%', x1: '65%', x2: '100%', delay: 210 },
  ],
  vertical: [
    { x: '10%', y1: '0%', y2: '40%', delay: 15 },
    { x: '20%', y1: '60%', y2: '100%', delay: 45 },
    { x: '80%', y1: '0%', y2: '35%', delay: 75 },
    { x: '90%', y1: '65%', y2: '100%', delay: 105 },
  ],
  diagonal: [
    { x1: '0%', y1: '30%', x2: '20%', y2: '10%', delay: 25 },
    { x1: '100%', y1: '30%', x2: '80%', y2: '10%', delay: 55 },
    { x1: '0%', y1: '70%', x2: '20%', y2: '90%', delay: 85 },
    { x1: '100%', y1: '70%', x2: '80%', y2: '90%', delay: 115 },
  ],
};

// Node positions (connection points)
const NODES = [
  { cx: '10%', cy: '15%' },
  { cx: '20%', cy: '25%' },
  { cx: '30%', cy: '35%' },
  { cx: '40%', cy: '15%' },
  { cx: '60%', cy: '25%' },
  { cx: '70%', cy: '45%' },
  { cx: '80%', cy: '35%' },
  { cx: '90%', cy: '25%' },
  { cx: '10%', cy: '55%' },
  { cx: '25%', cy: '65%' },
  { cx: '35%', cy: '75%' },
  { cx: '65%', cy: '65%' },
  { cx: '75%', cy: '75%' },
  { cx: '85%', cy: '85%' },
  { cx: '90%', cy: '65%' },
];

// Pulse path definitions
const PULSE_PATHS = [
  { startX: 0, startY: 15, endX: 40, endY: 15, duration: 120 },
  { startX: 100, startY: 25, endX: 60, endY: 25, duration: 150 },
  { startX: 0, startY: 35, endX: 30, endY: 35, duration: 100 },
  { startX: 100, startY: 45, endX: 70, endY: 45, duration: 130 },
  { startX: 0, startY: 55, endX: 25, endY: 55, duration: 110 },
  { startX: 100, startY: 65, endX: 75, endY: 65, duration: 140 },
  { startX: 10, startY: 0, endX: 10, endY: 40, duration: 120 },
  { startX: 90, startY: 0, endX: 90, endY: 35, duration: 130 },
];

// Animated Pulse Component
const Pulse = ({
  startX,
  startY,
  endX,
  endY,
  duration,
  delay,
  frame,
}: {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  duration: number;
  delay: number;
  frame: number;
}) => {
  const adjustedFrame = (frame - delay + 300) % 300;
  const loopFrame = adjustedFrame % duration;

  const progress = interpolate(loopFrame, [0, duration], [0, 1], {
    easing: Easing.inOut(Easing.ease),
    extrapolateRight: 'clamp',
  });

  const x = interpolate(progress, [0, 1], [startX, endX]);
  const y = interpolate(progress, [0, 1], [startY, endY]);

  const opacity = interpolate(
    progress,
    [0, 0.1, 0.9, 1],
    [0, 1, 1, 0],
    { extrapolateRight: 'clamp' }
  );

  return (
    <g>
      {/* Glow effect */}
      <circle
        cx={`${x}%`}
        cy={`${y}%`}
        r="8"
        fill={COLORS.cyanGlow}
        opacity={opacity * 0.5}
        filter="url(#glow)"
      />
      {/* Core pulse */}
      <circle
        cx={`${x}%`}
        cy={`${y}%`}
        r="3"
        fill={COLORS.cyan}
        opacity={opacity}
      />
    </g>
  );
};

// Main Neural Grid Composition
const NeuralGrid = () => {
  const frame = useCurrentFrame();

  // Subtle breathing effect for nodes
  const nodeScale = interpolate(
    Math.sin((frame / 30) * Math.PI),
    [-1, 1],
    [0.8, 1.2]
  );

  return (
    <AbsoluteFill>
      {/* Deep Blue Gradient Background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse at 50% 50%, ${COLORS.midBlue} 0%, ${COLORS.deepBlue} 70%),
            linear-gradient(180deg, ${COLORS.deepBlue} 0%, ${COLORS.midBlue} 50%, ${COLORS.deepBlue} 100%)
          `,
        }}
      />

      {/* Vignette overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at 50% 50%, transparent 30%, ${COLORS.deepBlue} 100%)`,
          opacity: 0.7,
        }}
      />

      {/* SVG Grid Network */}
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
        }}
        preserveAspectRatio="none"
      >
        <defs>
          {/* Glow filter for pulses */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Gradient for lines */}
          <linearGradient id="lineGradientH" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={COLORS.cyan} stopOpacity="0" />
            <stop offset="50%" stopColor={COLORS.cyan} stopOpacity="0.3" />
            <stop offset="100%" stopColor={COLORS.cyan} stopOpacity="0" />
          </linearGradient>

          <linearGradient id="lineGradientV" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={COLORS.cyan} stopOpacity="0" />
            <stop offset="50%" stopColor={COLORS.cyan} stopOpacity="0.3" />
            <stop offset="100%" stopColor={COLORS.cyan} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Horizontal Lines */}
        {GRID_LINES.horizontal.map((line, i) => (
          <line
            key={`h-${i}`}
            x1={line.x1}
            y1={line.y}
            x2={line.x2}
            y2={line.y}
            stroke={COLORS.cyan}
            strokeWidth="1"
            opacity="0.15"
          />
        ))}

        {/* Vertical Lines */}
        {GRID_LINES.vertical.map((line, i) => (
          <line
            key={`v-${i}`}
            x1={line.x}
            y1={line.y1}
            x2={line.x}
            y2={line.y2}
            stroke={COLORS.cyan}
            strokeWidth="1"
            opacity="0.15"
          />
        ))}

        {/* Diagonal Lines */}
        {GRID_LINES.diagonal.map((line, i) => (
          <line
            key={`d-${i}`}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke={COLORS.cyan}
            strokeWidth="1"
            opacity="0.1"
          />
        ))}

        {/* Connection Nodes */}
        {NODES.map((node, i) => (
          <circle
            key={`node-${i}`}
            cx={node.cx}
            cy={node.cy}
            r={2 * nodeScale}
            fill={COLORS.cyan}
            opacity={0.3 + (i % 3) * 0.1}
          />
        ))}

        {/* Animated Pulses */}
        {PULSE_PATHS.map((path, i) => (
          <Pulse
            key={`pulse-${i}`}
            startX={path.startX}
            startY={path.startY}
            endX={path.endX}
            endY={path.endY}
            duration={path.duration}
            delay={i * 30}
            frame={frame}
          />
        ))}
      </svg>

      {/* Subtle scan line effect */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(6, 182, 212, 0.02) 2px,
            rgba(6, 182, 212, 0.02) 4px
          )`,
          pointerEvents: 'none',
        }}
      />
    </AbsoluteFill>
  );
};

// Main Background Component with Remotion Player
export const LoginBackground = () => {
  return (
    <div
      className="fixed inset-0 z-0 overflow-hidden"
      style={{ background: COLORS.deepBlue }}
    >
      <Player
        component={NeuralGrid}
        durationInFrames={300}
        fps={30}
        compositionWidth={1920}
        compositionHeight={1080}
        loop
        autoPlay
        controls={false}
        style={{
          width: '100vw',
          height: '100vh',
          minWidth: '100%',
          minHeight: '100%',
          objectFit: 'cover',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) scale(1.1)',
        }}
      />
    </div>
  );
};

export default LoginBackground;
